import type {
  ComparisonOperator,
  EmittableHourConsumptionCondition,
  EmittableStreakCondition,
} from '@/alerts/treeTypes'
import { stripHours, type Calendar } from '@/alerts/calendar'

// The hourly alerts of docs/hourly-alerts-integration.md (tenant-db-schema,
// branch hourly-alerts). Two node choices select them: the consumption node's
// period (`periodId`) and the time node's unit (`unitId`). Both default to the
// day shapes, so a node saved before this feature keeps its meaning.

type Option = { id: string; label: string }

export const CONSUMPTION_PERIOD_OPTIONS: Option[] = [
  { id: 'day', label: 'יום' },
  { id: 'hour', label: 'שעה' },
]

export const STREAK_UNIT_OPTIONS: Option[] = [
  { id: 'days', label: 'ימים' },
  { id: 'hours', label: 'שעות' },
]

export const IGNORE_MISSING_LABELS: Record<StreakUnit, string> = {
  hours: 'דלג על שעות חסרות',
  days: 'דלג על ימים חסרים',
}

export const IGNORE_MISSING_HINTS: Record<StreakUnit, string> = {
  hours: 'שעה בלי קריאה לא שוברת את הרצף',
  days: 'יום בלי קריאה לא שובר את הרצף',
}

// A missing period breaks the streak unless the author skips it.
export function ignoresMissingPeriods(d: Data): boolean {
  return d.ignoreMissingPeriods === true
}

export type Period = 'day' | 'hour'
export type StreakUnit = 'days' | 'hours'

type Data = Record<string, unknown>

export function consumptionPeriod(d: Data): Period {
  return d.periodId === 'hour' ? 'hour' : 'day'
}

export function streakUnit(d: Data): StreakUnit {
  return d.unitId === 'hours' ? 'hours' : 'days'
}

// An hour check reads the meter's own hour use against one fixed limit. The
// engine's publish refuses everything else, so the editor refuses it first.
export type HourConsumptionConflict = 'kind' | 'percent' | 'average'

const HOUR_CONSUMPTION_CONFLICTS: ReadonlyArray<
  readonly [HourConsumptionConflict, (d: Data) => boolean]
> = [
  ['kind', d => d.firstSelectedId != null && String(d.firstSelectedId) !== '1'],
  ['percent', d => String(d.thirdSelectedId ?? '2') === '1'],
  ['average', d => Boolean(d.firstChecked) || Boolean(d.secondChecked)],
]

export function hourConsumptionConflicts(d: Data): HourConsumptionConflict[] {
  return HOUR_CONSUMPTION_CONFLICTS.filter(([, applies]) => applies(d)).map(
    ([conflict]) => conflict
  )
}

export function hourConsumptionCondition(
  d: Data,
  nodeId: string,
  operator: ComparisonOperator
): EmittableHourConsumptionCondition {
  const conflicts = hourConsumptionConflicts(d)
  if (conflicts.length > 0) {
    throw new Error(
      `hourConsumptionCondition: hour check on ${nodeId} cannot use ${conflicts.join(', ')}`
    )
  }
  const value = Number(d.value)
  return {
    kind: 'consumption',
    period: 'hour',
    consumption_type: 'meter',
    operator,
    reference_value:
      String(d.thirdSelectedId ?? '2') === '3' ? value / 1000 : value,
  }
}

export function hourStreakCondition(d: Data): EmittableStreakCondition {
  return {
    kind: 'streak',
    period: 'hour',
    count: Number(d.value),
    ignore_missing_periods: ignoresMissingPeriods(d),
  }
}

// What switching the choice writes into the node: a reset of what the other
// period cannot use, so no hidden field survives to be refused later.
export function consumptionPeriodPatch(period: Period, d: Data): Data {
  if (period === 'day') {
    const calendar = d.calendar as Calendar | undefined
    return calendar
      ? { periodId: 'day', calendar: stripHours(calendar) }
      : { periodId: 'day' }
  }
  return {
    periodId: 'hour',
    firstSelectedId: '1',
    thirdSelectedId:
      String(d.thirdSelectedId ?? '2') === '1' ? '2' : d.thirdSelectedId,
    firstChecked: false,
    secondChecked: false,
    windowStart: undefined,
    windowEnd: undefined,
    fourthSelectedId: undefined,
    fifthSelectedId: undefined,
    secondValue: undefined,
  }
}

export function streakUnitPatch(unit: StreakUnit): Data {
  return unit === 'days' ? { unitId: 'days' } : { unitId: 'hours' }
}
